import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/auth-store";

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

/**
 * 인증이 필요한 route를 보호하는 route guard.
 *
 * - `requireAuth`가 true(기본값)이고 사용자가 인증되지 않았으면,
 *   로그인 후 redirect를 위해 원래 location을 보존하며 `/login`으로 redirect한다.
 * - 인증 상태를 확인하는 동안 loading indicator를 표시한다.
 * - `requireAuth`가 false이면 children을 조건 없이 렌더링한다.
 */
export function AuthGuard({ children, requireAuth = true }: AuthGuardProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const location = useLocation();

  if (!requireAuth) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div
        data-testid="auth-loading"
        className="flex items-center justify-center min-h-screen"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
